import * as flexsearch from "flexsearch";

import { search as searchApi } from "apis-v2/callables";
import { fetchCompressedIndexData, getAllDocuments, getEncoderOptions, search } from "common/flexsearch";
import { WorkspaceResourceType } from "common/interfaces";
import { ISharedSlidesSearchDocument } from "common/interfaces/search";
import { serverContext } from "js/config";
import AppController from "js/core/AppController";

import DataService from "./DataService";

interface TeamSlidesDataServiceSearchQuery {
    resourceTypes: WorkspaceResourceType[],
    fullTextSearchQuery?: string,
    fullTextSearchByFields?: string[],
    tag?: string,
    size?: number
}

interface TeamSlidesDataServiceDataItem {
    id: string,
    libraryItemId: string,
    workspaceId: string,
    resource: ISharedSlidesSearchDocument["resource"],
    isTemplate: boolean,
    isUsedInPresentations: boolean,
    isDisabled: boolean,
    name: string,
    description: string,
    tags: string[],
    usedByCount: number,
    presentationCount: number,
    ratedByCount: number,
    ratedByUsers: {
        [userUid: string]: boolean
    },
    createdAt: number,
    modifiedAt?: number,
    contentModifiedAt?: number,
    createdBy: string
}

class TeamSlidesDataService extends DataService {
    protected async _prepareIndex(workspaceId: string) {
        const locale = serverContext.locale ?? "en";
        const { indexConfig, indexFileReadUrl } = await searchApi.getSharedSlidesSearchIndex({ workspaceId, locale });
        const data = await fetchCompressedIndexData(indexFileReadUrl);
        this._index = new flexsearch.Document({
            encoder: getEncoderOptions(locale),
            ...indexConfig,
        });
        data.forEach(({ key, data }) => this._index.import(key, data));
    }

    public async search(query: TeamSlidesDataServiceSearchQuery): Promise<TeamSlidesDataServiceDataItem[]> {
        await this.ensureIndexPrepared(AppController.workspaceId);

        let results: ISharedSlidesSearchDocument[];

        const limit = query.size ?? TeamSlidesDataService.SEARCH_QUERY_SIZE;

        if (query.fullTextSearchQuery) {
            results = search({
                index: this._index,
                query: query.fullTextSearchQuery,
                fields: query.fullTextSearchByFields
            });
        } else {
            results = getAllDocuments(this._index);
        }

        results = results.filter(doc => query.resourceTypes.includes(doc.resource.resourceType));

        if (query.tag) {
            results = results.filter(doc => doc.resource.tags.includes(query.tag));
        }

        results = results.slice(0, limit);

        const response: TeamSlidesDataServiceDataItem[] = [];

        results.forEach(({ slide, resource }) => {
            const usedPresentationIds = Object.keys(resource.metadata.usedInPresentations ?? {});
            const usedByUserIds = resource.metadata.usedByUsers ?? [];

            response.push({
                id: slide.id,
                libraryItemId: slide.id,
                workspaceId: AppController.workspaceId,
                resource,
                isTemplate: resource.resourceType === WorkspaceResourceType.SLIDE_TEMPLATE,
                isUsedInPresentations: usedPresentationIds.length > 0,
                isDisabled: !!resource.metadata.isDisabled,
                name: resource.metadata.name,
                description: resource.metadata.description,
                tags: resource.tags,
                usedByCount: usedByUserIds.length,
                presentationCount: usedPresentationIds.length,
                ratedByUsers: resource.metadata.ratedByUsers.reduce((acc, uid) => ({ ...acc, [uid]: true }), {}),
                ratedByCount: resource.metadata.ratedByUsers.length,
                createdAt: resource.createdAt,
                modifiedAt: resource.modifiedAt,
                contentModifiedAt: resource.metadata.contentModifiedAt,
                createdBy: resource.metadata.createdBy
            });
        });

        return response;
    }
}

export default TeamSlidesDataService;
